import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Download, BarChart3 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiDownload } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const RANGES = [
  { id: 'today', label: 'Today' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'quarterly', label: 'Quarterly' },
  { id: 'annually', label: 'Annually' },
  { id: 'custom', label: 'Custom' },
];

export default function TeamTaskReport() {
  const { user } = useAuth() || {};
  const [range, setRange] = useState('weekly');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [empId, setEmpId] = useState('');

  const isHrMd = ['hr', 'md'].includes((user?.department || '').toLowerCase())
    || ['super_admin', 'admin'].includes((user?.role || '').toLowerCase());

  useEffect(() => {
    if (isHrMd) {
      apiGet('/employees/lookup', { limit: 200 }).then((r) => {
        const list = r.items || [];
        setEmployees(list);
        if (list.length && !empId) setEmpId(list[0].id);
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [isHrMd]);

  const load = useCallback(async () => {
    // Skip initial fetch for HR/MD until the root employee picker auto-selects
    if (isHrMd && !empId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ range });
      if (range === 'custom') {
        if (!from || !to) { setLoading(false); return; }
        params.set('from', from);
        params.set('to', to);
      }
      if (isHrMd && empId) params.set('employee_id', empId);
      const r = await apiGet(`/tasks/report?${params.toString()}`);
      setReport(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load report');
    } finally { setLoading(false); }
  }, [range, from, to, empId, isHrMd]);

  useEffect(() => { load(); }, [load]);

  const downloadXlsx = async () => {
    try {
      const params = new URLSearchParams({ range });
      if (range === 'custom' && from && to) { params.set('from', from); params.set('to', to); }
      if (isHrMd && empId) params.set('employee_id', empId);
      await apiDownload(`/tasks/report/export.xlsx?${params.toString()}`, `task-report-${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Excel downloaded');
    } catch (e) { toast.error('Download failed'); }
  };

  return (
    <div>
      <PageHeader
        overline="Reports"
        title="Team Task Report"
        subtitle="Productivity snapshot for you and your direct + indirect reportees. Filter by date range and export to Excel."
      />

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="team-report-filters">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Range</label>
          <select value={range} onChange={(e) => setRange(e.target.value)} className="input min-w-[140px]" data-testid="team-report-range">
            {RANGES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </div>
        {range === 'custom' && (
          <>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">From</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">To</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input" />
            </div>
          </>
        )}
        {isHrMd && (
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Root Employee</label>
            <select value={empId} onChange={(e) => setEmpId(e.target.value)} className="input min-w-[200px]" data-testid="team-report-employee">
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.department}</option>)}
            </select>
          </div>
        )}
        <button onClick={downloadXlsx} disabled={!report} className="btn-secondary flex items-center gap-2 ml-auto" data-testid="team-report-export">
          <Download size={14} /> Excel
        </button>
      </div>

      {loading && <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>}

      {!loading && report && (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <KPI label="Created" value={report.totals.created} />
            <KPI label="Completed" value={report.totals.completed} accent="emerald" />
            <KPI label="Open" value={report.totals.open} />
            <KPI label="Overdue" value={report.totals.overdue} accent="rose" />
            <KPI label="Productivity" value={`${report.totals.productivity_pct}%`} accent="indigo" />
          </div>

          {/* Per-employee table */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <table className="w-full dense-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Dept</th>
                  <th>Designation</th>
                  <th>Reporting To</th>
                  <th className="text-right">Created</th>
                  <th className="text-right">Completed</th>
                  <th className="text-right">Open</th>
                  <th className="text-right">Overdue</th>
                  <th className="text-right">Prod %</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r) => (
                  <tr key={r.employee_id} data-testid={`team-report-row-${r.employee_id}`}>
                    <td className="font-display font-semibold">{r.name}</td>
                    <td className="text-xs">{r.department}</td>
                    <td className="text-xs">{r.designation || '—'}</td>
                    <td className="text-xs text-slate-500">{r.reporting_manager_name || '—'}</td>
                    <td className="text-right">{r.created}</td>
                    <td className="text-right text-emerald-700 font-semibold">{r.completed}</td>
                    <td className="text-right">{r.open}</td>
                    <td className="text-right text-rose-700">{r.overdue}</td>
                    <td className="text-right font-display font-bold">
                      <span className={`inline-block min-w-[44px] px-2 py-0.5 rounded ${r.productivity_pct >= 80 ? 'bg-emerald-50 text-emerald-700' : r.productivity_pct >= 50 ? 'bg-amber-50 text-amber-700' : 'bg-rose-50 text-rose-700'}`}>
                        {r.productivity_pct}%
                      </span>
                    </td>
                  </tr>
                ))}
                {report.rows.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-8 text-sm text-slate-500">No employees in your subtree.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-3 text-[11px] text-slate-500">
            <BarChart3 size={11} className="inline mr-1" />
            Window: <b>{report.range}</b> &middot; {report.from.slice(0, 10)} → {report.to.slice(0, 10)} &middot; {report.rows.length} employees
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, accent }) {
  const color = {
    emerald: 'text-emerald-700',
    rose: 'text-rose-700',
    indigo: 'text-[#3b4872]',
  }[accent] || 'text-slate-900';
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">{label}</div>
      <div className={`font-display font-bold text-2xl ${color}`}>{value}</div>
    </div>
  );
}
